import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Form, Modal } from "react-bootstrap";
import useManageBills from "../../hooks/patientDashboard/useManageBills";
import Spinner1 from "../common/spinners";
import useProfileStore from "../../store/patient/profileStore";
import moment from "moment";
import { formatCurrency } from "../../utils/utilities";
import { generatePaymentUrl, generateBulkPaymentUrl } from "../../utils/patients/generatePaymentUrl";
import BtnSpinner from "../common/btnSpinners";

const InvoiceTable = ({ header }) => {
  const { userBills, loading, findPatientBills } = useManageBills();
  const { profile } = useProfileStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    findPatientBills(profile?.id);
  }, []);

  const data = Array.isArray(userBills?.result) ? userBills.result : [];
  const filteredData = useMemo(
    () =>
      data.filter(
        (item) =>
          item?.bill_source?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.service?.includes?.(searchTerm)
      ),
    [data, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>{header || "Invoices"}</h5>
          <p>Review billed services and their payment state in a cleaner invoice table.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search invoices"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No invoices found</h6>
            <p>When billable services are added to your account, they will appear here.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((invoice) => (
                  <tr key={invoice?.id}>
                    <td>
                      <div className="data-cell-title">
                        {moment(invoice?.createdAt).format("Do MMM, YYYY")}
                      </div>
                      <div className="data-cell-meta">
                        {moment(invoice?.createdAt).format("dddd")}
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title text-capitalize">
                        {invoice?.bill_source || "---"}
                      </div>
                      <div className="data-cell-meta">{invoice?.service?.item?.name || "General invoice"}</div>
                    </td>
                    <td>
                      <div className="data-cell-title">{formatCurrency(invoice?.amount)}</div>
                    </td>
                    <td>
                      <span className={`data-status-badge ${invoice?.status === 1 ? "is-success" : "is-danger"}`}>
                        {invoice?.status === 1 ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const RecurringPaymentsTable = ({ header, loading, recurring }) => {
  const { profile } = useProfileStore();
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoadingBulk, setIsLoadingBulk] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const itemsPerPage = 5;

  const data = Array.isArray(recurring?.result) ? recurring.result : [];
  const filteredData = useMemo(
    () =>
      data.filter(
        (item) =>
          item?.bill_source?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.service?.item?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [data, searchTerm]
  );

  const unpaidItems = filteredData.filter((item) => item?.status !== 1);
  const totalPages = Math.max(1, Math.ceil(unpaidItems.length / itemsPerPage));
  const currentItems = unpaidItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Drop any selected ids that are no longer in the unpaid list (e.g. after refresh).
  const unpaidIds = useMemo(() => unpaidItems.map((item) => item?.id), [unpaidItems]);
  const validSelectedIds = useMemo(
    () => selectedIds.filter((id) => unpaidIds.includes(id)),
    [selectedIds, unpaidIds]
  );
  const selectedItems = unpaidItems.filter((item) => validSelectedIds.includes(item?.id));
  const selectedTotal = selectedItems.reduce((sum, item) => sum + Math.abs(item?.amount || 0), 0);
  const allSelected = unpaidItems.length > 0 && validSelectedIds.length === unpaidItems.length;

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : unpaidIds);
  };

  const generateUrl = (id) => {
    setProcessingId(id);
    setIsLoadingPayment(true);

    generatePaymentUrl(id)
      .then((response) => {
        window.location.href = `${response?.data?.data?.authorization_url}`;
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoadingPayment(false);
        setProcessingId(null);
      });
  };

  const handleConfirm = () => {
    if (!selectedPayment) return;
    generateUrl(selectedPayment.id);
    setShowConfirm(false);
  };

  const handleBulkPay = () => {
    if (validSelectedIds.length === 0) return;
    setIsLoadingBulk(true);

    generateBulkPaymentUrl(profile?.id, validSelectedIds)
      .then((response) => {
        const url = response?.data?.data?.authorization_url;
        if (url) {
          window.location.href = url;
        } else {
          console.error("No authorization URL returned", response?.data);
          setIsLoadingBulk(false);
          setShowBulkConfirm(false);
        }
      })
      .catch((error) => {
        console.error(error);
        setIsLoadingBulk(false);
        setShowBulkConfirm(false);
      });
  };

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>{header || "Billing & Insurance"}</h5>
          <p>Track outstanding bills, check service details, and process payments with a cleaner flow.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search bills or services"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {validSelectedIds.length > 0 && (
        <div
          className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 p-3"
          style={{ background: "rgba(12,183,133,0.08)", border: "1px solid rgba(12,183,133,0.25)", borderRadius: 12 }}
        >
          <div>
            <strong>{validSelectedIds.length}</strong> bill{validSelectedIds.length > 1 ? "s" : ""} selected
            {" — "}
            <strong>{formatCurrency(selectedTotal)}</strong>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button variant="light" size="sm" onClick={() => setSelectedIds([])} disabled={isLoadingBulk}>
              Clear
            </Button>
            <Button variant="primary" onClick={() => setShowBulkConfirm(true)} disabled={isLoadingBulk}>
              {isLoadingBulk ? <BtnSpinner /> : `Pay ${validSelectedIds.length} bill${validSelectedIds.length > 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      )}

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No pending bills</h6>
            <p>Once a bill is awaiting payment, it will show up here with its next action.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <Form.Check
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all pending bills"
                    />
                  </th>
                  <th>Date</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((payment) => (
                  <tr key={payment?.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={validSelectedIds.includes(payment?.id)}
                        onChange={() => toggleOne(payment?.id)}
                        aria-label={`Select bill ${payment?.id}`}
                      />
                    </td>
                    <td>
                      <div className="data-cell-title">{moment(payment?.createdAt).format("Do MMM, YYYY")}</div>
                      <div className="data-cell-meta">{moment(payment?.createdAt).format("dddd")}</div>
                    </td>
                    <td>
                      <div className="data-cell-title text-capitalize">{payment?.bill_source}</div>
                      <div className="data-cell-meta">{payment?.service?.item?.name || "Hospital service"}</div>
                    </td>
                    <td>
                      <div className="data-cell-title">{formatCurrency(payment?.amount)}</div>
                    </td>
                    <td>
                      <span className="data-status-badge is-warning">Pending Payment</span>
                    </td>
                    <td>
                      <Button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowConfirm(true);
                        }}
                        variant="primary"
                      >
                        {isLoadingPayment && processingId === payment?.id ? <BtnSpinner /> : "Process Payment"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && unpaidItems.length > 0 && (
          <div className="data-pagination">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to process payment for <strong>{selectedPayment?.bill_source}</strong> -
          {" "}
          {formatCurrency(selectedPayment?.amount)}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {isLoadingPayment && processingId === selectedPayment?.id ? <BtnSpinner /> : "Yes, Process"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBulkConfirm} onHide={() => !isLoadingBulk && setShowBulkConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You're about to pay <strong>{validSelectedIds.length}</strong> bill
            {validSelectedIds.length > 1 ? "s" : ""} totalling{" "}
            <strong>{formatCurrency(selectedTotal)}</strong> in a single transaction.
          </p>
          <ul className="mb-0" style={{ maxHeight: 180, overflowY: "auto", paddingLeft: 18 }}>
            {selectedItems.map((item) => (
              <li key={item?.id} className="text-capitalize">
                {item?.bill_source || "Bill"} — {formatCurrency(item?.amount)}
              </li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkConfirm(false)} disabled={isLoadingBulk}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleBulkPay} disabled={isLoadingBulk}>
            {isLoadingBulk ? <BtnSpinner /> : `Pay ${formatCurrency(selectedTotal)}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export { InvoiceTable, RecurringPaymentsTable };
